export const addParamsToUrl = (
  baseUrl: string,
  params: Record<string, string>
): string => {
  const url = new URL(baseUrl);
  Object.keys(params).forEach((key) => {
    url.searchParams.set(key, params[key]);
  });
  return url.toString();
};

export interface ExtensionPopupContentProps {
  reSync?: boolean;
  redirectUrl: string;
  extTargetUrl: string;
  targetStartPage:
    | "profile" // flow - profile -> audience -> posts
    | "audience" // flow - audience -> posts
    | "posts"
    | "singlePost" // flow - singlePost -> postSummary
    | "postSummary"
    | "comments"
    | "postReactors";
  otherRedirectParams?: object;
  closeCurrentWindow?: boolean;
  onOpenChange?: (open: boolean) => void;
  open: boolean;
  checkType?: "GuestFlow" | "MediaKitFlow";
}

export const ExactPricingList = [
  `We analyze your LinkedIn profile and posts to provide holistic earnings estimates.`,
  `Pricing is dynamic, so it'll change as your profile grows. To stay updated, check regularly.`,
  `Use this as a private benchmark to guide your rates with brands.`,
  `Start with installing our Chrome extension & proceed to see your exact earnings.`,
];

export interface PostFormatTypes {
  key: "image" | "video" | "carousel";
  value: string;
}

interface FAQ {
  question: string;
  answer: string | JSX.Element;
}

export const PriceCalculatorFaqs = (): FAQ[] => {
  return [
    {
      question:
        "What’s the best way to calculate LinkedIn sponsored post rates?",
      answer: (
        <p>
          The best way to calculate LinkedIn sponsored post rates is through
          data-backed insights rather than guesswork. This is exactly why we
          developed the LinkedIn Pricing Calculator.
          <br />
          <br />
          A simple search shows the market is flooded with calculators for
          Instagram, YouTube, and other platforms, but LinkedIn creators have
          nothing to help them set a benchmark. As a result, many LinkedIn
          influencers rely on advice from peers or seniors, which can be
          inconsistent and often lacks accuracy.
          <br />
          <br />
          Our calculator changes this by analyzing vast datasets of LinkedIn
          creator profiles, industry standards, and current market trends.
          Analysing a few key details, this tool provides an estimated range of
          what you can reasonably ask for a sponsored post on LinkedIn. It is
          designed to give LinkedIn creators a reliable starting point for
          setting their rates confidently, reflecting their true market value.
        </p>
      ),
    },
    {
      question: "How does the LinkedIn Pricing Calculator estimate earnings?",
      answer: (
        <p>
          These estimates are based on data from thousands of posts, and
          collaborations with the influencers on our platform. They’re
          calculated by measuring the relationship between impressions, content
          niche and follower size. These parameters—along with extensive
          industry data, creator market trends—help provide a well-rounded
          estimate of potential earnings per post for LinkedIn creators to
          understand what they could charge for brand collaborations. Use it
          only as a guide.
          <br />
          <br />
          However, for a more accurate and customized estimate, we recommend
          using our advanced, automated version. This free tool dives deeper,
          analyzing 100+ data points specific to your LinkedIn profile to give a
          precise valuation of your earning potential.
          <br />
          <br />
          With this more tailored approach, LinkedIn creators can confidently
          gauge their potential earnings, particularly through the anchors
          platform, and make informed decisions about their pricing strategies.
        </p>
      ),
    },
    {
      question:
        "What factors impact my estimated LinkedIn earnings per sponsored post?",
      answer: (
        <div>
          <p>
            Your estimated LinkedIn earnings per sponsored post are influenced
            by a wide range of factors that brands consider when evaluating your
            profile’s value. These include:
          </p>
          <br />
          <ol className="list-decimal">
            <li className="ms-6">
              <p>Follower count</p>
            </li>
            <li className="ms-6">
              <p>Engagement rate</p>
            </li>
            <li className="ms-6">
              <p>Average post reach</p>
            </li>
            <li className="ms-6">
              <p>Audience demographics (age, location, industry)</p>
            </li>
            <li className="ms-6">
              <p>Niche or industry relevance</p>
            </li>
            <li className="ms-6">
              <p>Post frequency</p>
            </li>
            <li className="ms-6">
              <p>Post type (video, text, image, carousel)</p>
            </li>
            <li className="ms-6">
              <p>Content quality</p>
            </li>
            <li className="ms-6">
              <p>Profile completeness</p>
            </li>
            <li className="ms-6">
              <p>Professional experience</p>
            </li>
            <li className="ms-6">
              <p>Skills endorsements</p>
            </li>
            <li className="ms-6">
              <p>LinkedIn recommendations</p>
            </li>
            <li className="ms-6">
              <p>Consistency in posting</p>
            </li>
            <li className="ms-6">
              <p>Brand alignment</p>
            </li>
            <li className="ms-6">
              <p>Audience sentiment</p>
            </li>
            <li className="ms-6">
              <p>Language proficiency</p>
            </li>
            <li className="ms-6">
              <p>Post engagement patterns over time</p>
            </li>
            <li className="ms-6">
              <p>Content format diversity</p>
            </li>
            <li className="ms-6">
              <p>Percentage of organic followers vs. bots</p>
            </li>
            <li className="ms-6">
              <p>Audience growth rate</p>
            </li>
            <li className="ms-6">
              <p>Post timing and scheduling</p>
            </li>
            <li className="ms-6">
              <p>Click-through rate (CTR) on posts</p>
            </li>
            <li className="ms-6">
              <p>Hashtag effectiveness</p>
            </li>
            <li className="ms-6">
              <p>Networking activity (comments, likes)</p>
            </li>
            <li className="ms-6">
              <p>Profile views &amp; search appearances</p>
            </li>
            <li className="ms-6">
              <p>Industry authority and expertise</p>
            </li>
            <li className="ms-6">
              <p>Influencer endorsements</p>
            </li>
            <li className="ms-6">
              <p>Total post impressions</p>
            </li>
            <li className="ms-6">
              <p>Response rate to comments &amp; messages</p>
            </li>
            <li className="ms-6">
              <p>&hellip;and so many more</p>
            </li>
          </ol>
          <br />
          <p>
            These, and many more factors, impact your estimated earnings, which
            is why an automated method is essential for a truly accurate
            measure. By analyzing a vast range of data points, we deliver a
            tailored estimate that reflects the full scope of your profile’s
            value.
            <br />
            <br />
            However, for a simpler and broader estimate, you can use our manual
            method, which takes into account four main parameters to provide a
            baseline for your potential earnings.
          </p>
        </div>
      ),
    },
    {
      question:
        "How accurate is the estimated range, and how can I get a more precise estimate of my LinkedIn earnings?",
      answer: (
        <div>
          <ol start={1}>
            <li className="ms-4">
              <div>
                <div>
                  The estimated earnings range from our calculator is based on 4
                  primary inputs and provides a solid starting point. However,
                  earnings can vary depending on factors, like
                  post-type&mdash;video content, for example, generally commands
                  higher rates than text-based posts. For a more precise
                  estimate, we need to understand your profile beyond just
                  followers and impressions.
                </div>
                <div>
                  If you&rsquo;re curious about diving deeper, check out the{" "}
                  <u>auto-mode</u> of our LinkedIn Pricing Calculator to explore
                  a more detailed analysis. This free tool provides insights
                  into what&rsquo;s resonating with your audience so you can
                  understand your audience, track engagement trends on your
                  profile, and see your earning potential, all in one place.
                </div>
              </div>
            </li>
          </ol>
        </div>
      ),
    },
    {
      question:
        "How can LinkedIn influencers estimate their charges for brand collaborations?",
      answer: (
        <div>
          <div>
            <div>
              LinkedIn influencers can estimate their charges for brand
              collaborations by evaluating several essential factors that
              determine their profile&rsquo;s value to brands. Key metrics
              include follower count, audience demographics, average impressions
              and engagement rate&mdash;each of which provides insights into
              your influence and reach on LinkedIn.&nbsp;
            </div>
            <p>
              <br />
            </p>
            <div>
              Additionally, the type of content you create (such as video posts,
              articles, or images) can impact pricing, as certain formats tend
              to command higher rates due to their engagement potential. To
              gauge the prices you should set, it&rsquo;s also helpful to
              research average LinkedIn influencer prices in your community,
              giving you a sense of standard pricing for collaborations.
            </div>
            <p>
              <br />
            </p>
            <div>
              Brands typically look for influencers with active and relevant
              audiences, making engagement rate a significant factor in setting
              rates. For a quick and reliable estimate, our LinkedIn Pricing
              Calculator simplifies this process, leveraging data-driven
              insights from profile metrics, audience details, and market trends
              to help you determine a fair price for sponsored posts.
            </div>
          </div>
        </div>
      ),
    },
    {
      question:
        "Is LinkedIn engagement rate important for setting influencer prices?",
      answer: (
        <div>
          <div>
            Yes, LinkedIn engagement rate plays a crucial role in setting
            influencer prices.&nbsp;
          </div>
          <div>
            Engagement rate measures how actively your audience interacts with
            your content&mdash;through likes, comments, shares, and clicks. For
            brands, a high engagement rate indicates that followers aren&rsquo;t
            just viewing content, they&rsquo;re genuinely interested and
            involved, which increases the impact of sponsored posts.
          </div>
          <p>
            <br />
          </p>
          <div>
            LinkedIn influencers with high engagement rates can often command
            higher rates because engaged audiences drive stronger brand
            visibility and connections. While follower count remains essential,
            engagement rate often provides a more reliable indicator of an
            influencer&rsquo;s true influence. Our{" "}
            <u>LinkedIn pricing calculator (auto-mode)</u> includes this metric
            to ensure that influencers with strong engagement levels are
            accurately valued and compensated for brand collaborations.
          </div>
        </div>
      ),
    },
    {
      question:
        "Can the LinkedIn pricing calculator help beginners or those with smaller followings?",
      answer: (
        <div>
          <p>
            Absolutely, our LinkedIn Pricing Calculator is designed to help both
            established influencers and beginners with smaller followings. Many
            brands now recognize the value of &quot;micro &amp; nano
            influencers&quot; on LinkedIn&mdash;creators with smaller yet highly
            engaged audiences. Even with fewer followers, beginners can still
            attract brand collaborations if their content resonates with a
            specific niche or industry.
          </p>
          <br />
          <p>
            Our LinkedIn Pricing Calculator takes into account engagement
            metrics, content quality, and audience relevance, not just follower
            count (in its auto-mode). Our aim with this free tool is to empower
            LinkedIn influencers&mdash;helping them gauge their true worth and
            avoid undervaluing themselves.&nbsp;
          </p>
          <br />
          <p>
            By providing a reliable, data-driven estimate, our calculator
            supports influencers in setting fair rates and avoiding unfair
            industry practices. Additionally, influencers who join anchors gain
            access to even more collaboration opportunities and tools tailored
            to help them grow.
          </p>
        </div>
      ),
    },
    {
      question:
        "What types of brands typically collaborate with LinkedIn influencers?",
      answer: (
        <div>
          <div>
            All sorts of brands collaborate with LinkedIn influencers, ranging
            from B2B, B2C, to D2C companies. These collaborations include
            startups, established corporations, and everything in between, as
            LinkedIn has become a versatile platform for different marketing
            goals. Brands engage with LinkedIn influencers for various purposes,
            such as building brand awareness, enhancing PR, driving sales, or
            even recruiting talent.
          </div>
          <p>
            <br />
          </p>
          <div>
            For instance, companies like Flipkart, Swiggy, Panasonic, Rapido,
            Preplaced, and Pot &amp; Bloom have collaborated with LinkedIn
            creators to connect with targeted audiences and achieve specific
            objectives.&nbsp;
          </div>
          <div>
            Whether it&rsquo;s a tech startup aiming to reach niche
            professionals or a large consumer brand focused on increasing
            visibility, LinkedIn influencers offer a valuable way to engage
            users in a professional setting.
          </div>
        </div>
      ),
    },
    {
      question:
        "How can creators increase their earnings potential on LinkedIn?",
      answer: (
        <div>
          <div>
            To boost earnings potential on LinkedIn, the first step is defining
            your niche through your content. This could mean focusing on a
            highly specific area like &ldquo;AI in cybersecurity,&rdquo;
            choosing a broader theme like &ldquo;early career guidance,&rdquo;
            or combining a few overlapping topics&mdash;such as health &amp;
            wellness, productivity, and mindfulness&mdash;within your personal
            journey.
          </div>
          <p>
            <br />
          </p>
          <div>
            Understanding your audience&rsquo;s interests and engagement
            patterns is equally important, and this is where our{" "}
            <u>LinkedIn pricing calculator&rsquo;s auto-mode</u> becomes
            invaluable. The auto-mode can help you in two main ways: first, by
            analyzing your audience&rsquo;s likes, responses, and interactions
            to show you what resonates most, and second, by providing a precise
            estimate of your profile&rsquo;s worth to brands. This data-driven
            approach gives you insights into trending content and major
            demographics, enabling you to align with what&rsquo;s working best
            for your audience.
          </div>
          <p>
            <br />
          </p>
          <div>
            Our LinkedIn Pricing Calculator offers a reliable estimate of your
            earnings potential, especially helpful when approaching brand
            collaborations. With a clear niche and a strong understanding of
            your audience, backed by our advanced calculator, you can maximize
            your value and confidently set competitive rates that reflect your
            true market worth.
          </div>
        </div>
      ),
    },
    {
      question: "How often should LinkedIn creators update their rates?",
      answer: (
        <div>
          <div>
            LinkedIn creators should consider updating their rates whenever key
            metrics shift, as these changes impact the value of their profile to
            brands. Factors like follower count, engagement rate, and average
            impressions play a significant role in determining what you should
            charge for sponsored posts. For instance, if your followers are
            growing but impressions remain stable, or if impressions increase
            drastically without a significant follower jump, it may be time to
            adjust your rates accordingly.
          </div>
          <p>
            <br />
          </p>
          <div>
            Our free LinkedIn pricing calculator offers a strong starting point
            for estimating your rate. For a more precise understanding, the{" "}
            <u>auto-mode</u> of our calculator provides a deeper analysis of
            your profile. This advanced option not only gives a closer estimate
            of your potential earnings but also highlights which content
            resonates most with your audience.&nbsp;
          </div>
          <p>
            <br />
          </p>
          <div>
            By tracking engagement trends and key metrics, you can refine your
            approach, maximize your profile&rsquo;s value, and ensure your
            charges reflect your true worth.
          </div>
        </div>
      ),
    },
    {
      question:
        "Why do I need the auto-mode of the LinkedIn pricing calculator?",
      answer: (
        <div>
          <div>
            The auto-mode of our LinkedIn pricing calculator enables influencers
            get a fair and accurate valuation of their profile. While a broad
            estimate can be generated using a few key metrics in its default
            mode, a LinkedIn profile of an influencer is complex, with many
            nuances and subjectivities. True value can only be determined after
            a comprehensive analysis&mdash;a process that&rsquo;s only possible
            with technology.
          </div>
          <div>
            Our auto-mode runs through more than 100 data points on your
            profile, something that&rsquo;s nearly impossible to achieve
            manually.&nbsp;
          </div>
          <p>
            <br />
          </p>
          <div>
            This mode not only provides a much closer rate estimate, reflecting
            your profile&rsquo;s worth to brands, but also offers valuable
            insights into what&rsquo;s resonating with your audience, engagement
            trends, and major audience demographics.&nbsp;
          </div>
          <p>
            <br />
          </p>
          <div>
            For influencers serious about understanding their impact and setting
            fair, data-driven rates, the <u>auto-mode</u> is an invaluable tool
            that goes beyond basic estimation.
          </div>
        </div>
      ),
    },
    {
      question: "How can I determine if my LinkedIn rates are competitive?",
      answer: (
        <div>
          <div>
            To determine if your LinkedIn rates are competitive, start by
            analyzing a few core factors: your follower count, engagement rate,
            content quality, and niche. Comparing these with industry benchmarks
            can give you a general idea of average rates within your area, but
            this method alone may not capture your unique profile value.
          </div>
          <p>
            <br />
          </p>
          <div>
            Our LinkedIn Pricing Calculator provides a reliable estimate,
            factoring in key metrics and current market trends to help you set
            competitive rates for sponsored posts. For a deeper analysis, try
            the <u>auto-mode version</u> which understands your profile
            holistically, offering insights on what resonates with your
            audience, trending content types, and demographic details.&nbsp;
          </div>
          <p>
            <br />
          </p>
          <div>
            This detailed approach will allow you to confidently assess your
            rates and make adjustments to align with industry standards,
            ensuring you&rsquo;re fairly compensated for your influence and
            reach on LinkedIn.
          </div>
        </div>
      ),
    },
    {
      question:
        "What metrics should I track to attract more brand partnerships on LinkedIn?",
      answer: (
        <div>
          <div>
            Brand partnerships are done to generate impact. And your impact as
            an influencer is shaped by several factors. Following are some key
            metrics:
          </div>
          <br />
          <ol start={1} className="list-decimal">
            <li className="ms-4">
              <p>
                <strong>Follower Count:</strong> Brands often use follower count
                to gauge reach. Micro-influencers (10k-50k followers) may have
                lower rates, but a targeted audience can increase value.
              </p>
            </li>
            <li className="ms-4">
              <p>
                <strong>Engagement Rate:</strong> This reflects how actively
                your followers interact with your posts. A strong engagement
                rate signals a loyal audience, making your profile more valuable
                to brands and justifying higher rates.
              </p>
            </li>
            <li className="ms-4">
              <p>
                <strong>Reach and Impressions:</strong> These indicate how many
                people see your content. Higher reach and impressions make your
                profile more attractive to brands.
              </p>
            </li>
            <li className="ms-4">
              <p>
                <strong>Audience Insights and Engagement Trends:</strong>{" "}
                Knowing what resonates with your audience allows you to
                understand their preferences and engagement patterns. This
                positions you as an expert, helping you create targeted content
                that not only improves all of the above 3 metrics, but also
                attracts higher-paying brand partnerships.
              </p>
            </li>
          </ol>
          <br />
          <div>
            Tracking these metrics consistently helps you stand out and attract
            meaningful brand collaborations on LinkedIn.
          </div>
        </div>
      ),
    },
    {
      question:
        "How many followers do I need on LinkedIn to start earning from brand partnerships?",
      answer: (
        <div>
          <div>
            There&rsquo;s no minimum follower count required to earn from brand
            partnerships on LinkedIn. Influencers of all sizes&mdash;nano,
            micro, and macro&mdash;each bring unique value to brands:
          </div>
          <p>
            <br />
          </p>
          <ul className="list-disc">
            <li className="ms-4">
              <p>
                <strong>Nano-influencers (1k-10k followers):</strong> They have
                small but highly engaged audiences, therefore, strong community
                trust. It is ideal for brands wanting authentic engagement with
                niche audiences.
              </p>
            </li>
            <li className="ms-4">
              <p>
                <strong>Micro-influencers (10k-50k followers):</strong> These
                combine reach with personal connections, allowing brands to
                achieve wider awareness while still driving meaningful
                engagement. They&rsquo;re perfect for brands looking to grow
                credibility and visibility.
              </p>
            </li>
            <li className="ms-4">
              <p>
                <strong>Macro-influencers (50k+ followers):</strong> These offer
                large-scale reach, helping brands amplify their message across a
                broad audience, especially useful for high-impact campaigns.
              </p>
            </li>
          </ul>
          <p>
            <br />
          </p>
          <div>
            Ultimately, success in brand partnerships on LinkedIn isn&rsquo;t
            solely about follower count.&nbsp;
          </div>
          <div>
            Understanding your strengths and how your unique audience aligns
            with brand goals is key. Brands value authentic &amp; targeted
            creators, making every follower count when paired with the right
            strategy.
          </div>
        </div>
      ),
    },
    {
      question:
        "Are LinkedIn rates different for influencers with niche audiences?",
      answer: (
        <p>
          Yes, LinkedIn rates can vary for influencers with niche audiences.
          Influencers who cater to a specific niche often command higher rates,
          as their targeted content attracts a highly engaged and relevant
          audience that aligns well with certain brands. Brands value niche
          influencers because they can deliver focused messaging to a
          well-defined group, increasing the likelihood of genuine engagement
          and conversions.
          <br />
          <br />
          For instance, an influencer specializing in “tech for healthcare” or
          “AI in finance” may charge more than a general influencer, as their
          audience is highly specific and valuable to brands within that
          industry. Niche influencers can often justify premium rates due to
          their expertise and the direct connection they maintain with their
          audience, which can drive more impactful results for brand
          partnerships.
        </p>
      ),
    },
  ];
};
